import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { CodeBlock } from "@/components/storyboard/code/CodeBlock";
import { CodeMarkdown } from "@/components/storyboard/code/CodeMarkdown";

interface ReviewOption {
  label: string;
  selected: boolean;
  correct: boolean;
}

interface QuizReviewOptionListProps {
  options: ReviewOption[];
  codeSnippet?: string;
}

type OptionReviewKind = "correct" | "wrong" | "neutral";

const optionReviewKind = (option: ReviewOption): OptionReviewKind => {
  const chosenAnswerIsCorrect = option.selected && option.correct;
  if (chosenAnswerIsCorrect) return "correct";
  if (option.selected) return "wrong";
  return "neutral";
};

const optionReviewSurface = (kind: OptionReviewKind) => {
  if (kind === "correct") return "border-success bg-success-light";
  if (kind === "wrong") return "border-error bg-error-light";
  return "border-gray-200 bg-gray-50";
};

const optionReviewIconCircle = (kind: OptionReviewKind) => {
  if (kind === "correct")
    return "border-success bg-success text-success-solid-foreground";
  if (kind === "wrong")
    return "border-error bg-error text-error-solid-foreground";
  return "border-gray-300 bg-white";
};

const optionReviewBadge = (kind: OptionReviewKind) => {
  if (kind === "correct") return "bg-success/15 text-success-foreground";
  return "bg-error/15 text-error-foreground";
};

const renderOptionReviewIcon = (kind: OptionReviewKind) => {
  if (kind === "correct") return <Check className="h-4 w-4" />;
  if (kind === "wrong") return <X className="h-4 w-4" />;
  return null;
};

const ReviewOptionRow = ({ option }: { option: ReviewOption }) => {
  const kind = optionReviewKind(option);

  return (
    <div
      className={cn(
        "relative flex items-center gap-4 rounded-xl border-2 p-4 text-left",
        optionReviewSurface(kind)
      )}
    >
      <div
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2",
          optionReviewIconCircle(kind)
        )}
      >
        {renderOptionReviewIcon(kind)}
      </div>
      <div className="prose min-w-0 max-w-full flex-1 text-foreground prose-p:my-0">
        <CodeMarkdown>{option.label}</CodeMarkdown>
      </div>
      {option.selected && (
        <span
          className={cn(
            "shrink-0 rounded-full px-2.5 py-0.5 font-semibold",
            optionReviewBadge(kind)
          )}
        >
          Deine Antwort
        </span>
      )}
    </div>
  );
};

/**
 * Read-only option rows for a completed quiz review. Success/error tints
 * apply only to the learner's pick — unselected correct answers stay
 * neutral, including when the question as a whole is wrong.
 */
const QuizReviewOptionList = ({
  options,
  codeSnippet,
}: QuizReviewOptionListProps) => (
  <div className="flex flex-col gap-3">
    {codeSnippet && (
      <div className="mb-1">
        <CodeBlock code={codeSnippet} />
      </div>
    )}
    {options.map((option, index) => (
      <ReviewOptionRow key={index} option={option} />
    ))}
  </div>
);

export default QuizReviewOptionList;
